import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { Clock, Users, CheckCircle, AlertCircle, MessageSquare, BarChart3, FileText, Plus, Send, ThumbsUp, ThumbsDown, User, Settings, LogOut, ChevronRight, ChevronDown, ChevronUp, X, Mic, MicOff, Mail, Search, RefreshCw, Trash2, XCircle, ArrowLeft, Loader2, Bell, BellOff, Eye, Download, Filter, Columns, ArrowUpDown, Maximize2, Minimize2, ChevronLeft, ShieldAlert, Heart, Phone } from 'lucide-react';
import { useToast, useCrisisAlert, ToastProvider, CrisisContext } from '../shared.jsx';
import { storage, addAuditEntry, calculateWorkingHours, OWL_LOGO, EMAIL_CONFIG, detectHarmContent, notifyMentorOfHarmDetection, sendNotification, VoiceInputButton, useVoiceInput, initFCM, removeFCMToken, notificationPermissionGranted, requestNotificationPermission, CrisisResourceModal, getUrlParams, clearUrlParams, initFirebase } from '../shared.jsx';

function getConflictAction(conflict, user) {
  if (!conflict || !user) return null;
  
  const isMentee = conflict.mentees?.some(m => m.id === user.id || m.email?.toLowerCase() === user.email?.toLowerCase());
  const isCreator = conflict.createdBy === user.id;
  const canParticipate = isMentee || isCreator;
  
  // Non-participating roles (admin, mentor, fly-on-wall, omniscient) never have actions
  if (!canParticipate) return null;

  // Terms acceptance — only mentees who aren't the creator
  if (isMentee && !isCreator && conflict.termsAcceptance?.status === 'pending' && !conflict.termsAcceptance?.acceptedBy?.includes(user.id)) {
    return 'Review & Accept Terms';
  }
  if (conflict.termsAcceptance?.status === 'changes-proposed' && conflict.createdBy === user.id) {
    return 'Review Proposed Changes';
  }

  const status = conflict.status;

  if (status === 'identify-define' || status === 'created') {
    const userResponse = conflict.steps?.identifyDefine?.data?.[user.id];
    if (!userResponse && canParticipate) return 'Submit Your Understanding';
    
    const data = conflict.steps?.identifyDefine?.data || {};
    const menteeIds = conflict.mentees?.map(m => m.id) || [];
    // Only count responses from current participants (ignore removed participants' data)
    const activeResponseIds = Object.keys(data).filter(k => menteeIds.includes(k));
    const responseCount = activeResponseIds.length;
    const totalMentees = menteeIds.length || 1;
    if (responseCount >= totalMentees) {
      // Check if THIS user still needs to acknowledge someone
      for (const responderId of activeResponseIds) {
        const acks = (data[responderId].acknowledgments || []).map(a => a.userId);
        if (responderId !== user.id && !acks.includes(user.id)) return 'Acknowledge Paraphrases';
      }

      // Check if ALL mentees have acknowledged ALL others before showing vote
      let allAcknowledged = true;
      for (const responderId of activeResponseIds) {
        for (const otherId of menteeIds) {
          if (otherId === responderId) continue;
          const acks = (data[responderId].acknowledgments || []).map(a => a.userId);
          if (!acks.includes(otherId)) {
            allAcknowledged = false;
            break;
          }
        }
        if (!allAcknowledged) break;
      }
      
      if (!allAcknowledged) return 'Waiting for Others to Acknowledge';
      
      // All acknowledged — check skip communication vote
      const votes = conflict.steps?.identifyDefine?.skipCommunicationVotes;
      if (!votes && canParticipate) return 'Vote Required';
      if (votes && !votes[user.id] && canParticipate) return 'Vote Required';
    }
    return null;
  }

  if (status === 'communicate') {
    const cycles = conflict.steps?.communicate?.cycles || [];
    if (cycles.length === 0) {
      const activeComposer = conflict.steps?.communicate?.activeComposer;
      if (activeComposer === user.id) return 'Continue Your Statement';
      const skipVotes = conflict.steps?.identifyDefine?.skipCommunicationVotes || {};
      const noVoter = Object.values(skipVotes).find(v => !v.skip);
      if (noVoter?.userId === user.id) return 'Start Communication';
      const commEntry = conflict.statusHistory?.filter(e => e.status === 'communicate').pop();
      if (commEntry && (new Date() - new Date(commEntry.timestamp)) / 60000 > (conflict.responseWindow || 15)) {
        return 'Communication Entry Overdue';
      }
      return null;
    }
    const lastCycle = cycles[cycles.length - 1];
    if (lastCycle && !lastCycle.paraphrase && lastCycle.userId !== user.id && canParticipate) return 'Paraphrase Response';
    
    const satVotes = conflict.steps?.communicate?.satisfactionVotes || {};
    // Only prompt for satisfaction vote when the last cycle is complete (has a paraphrase)
    // If a new Because statement is pending paraphrase, we're mid-exchange — no vote needed
    if (lastCycle?.paraphrase && !satVotes[user.id] && Object.keys(satVotes).length > 0 && canParticipate) return 'Vote Required';
    
    if (lastCycle?.paraphrase && cycles.length < (conflict.maxCommunicationCycles || 5) * 2 && canParticipate) {
      const lastParaphraserId = lastCycle?.paraphrasedBy;
      if (lastParaphraserId === user.id) return 'Share Your Perspective';
    }
    return null;
  }

  if (status === 'explore-alternatives') {
    const doneUsers = conflict.steps?.exploreAlternatives?.doneUsers || [];
    if (doneUsers.includes(user.id)) return null;
    const userAlts = (conflict.steps?.exploreAlternatives?.alternatives || []).filter(a => a.createdBy === user.id);
    if (userAlts.length < 3 && canParticipate) return userAlts.length === 0 ? 'Propose Solutions' : 'Propose More or Finish';
    return null;
  }

  if (status === 'evaluate-select') {
    if (!conflict.steps?.evaluateSelect?.ratings?.[user.id] && canParticipate) return 'Rate Solutions';
    return null;
  }

  if (status === 'agree-implement') {
    if (!conflict.steps?.agreeImplement?.acknowledged?.find(a => a.userId === user.id) && canParticipate) return 'Acknowledge Resolution';
    return null;
  }

  if (status === 'follow-up') {
    if (!conflict.steps?.followUp?.ratings?.[user.id] && canParticipate) return 'Complete Follow-up';
    return null;
  }

  return null;
}

function ConflictCard({ conflict, user, onClick }) {
  const actionNeeded = getConflictAction(conflict, user);
  const isWaitingState = actionNeeded && actionNeeded.startsWith('Waiting');
  const hasUserAction = actionNeeded && !isWaitingState;
  // Feature 5: Unreviewed harm events badge
  const unreviewedHarmCount = (conflict.harmEvents || []).filter(e => !e.mentorReviewed).length;
  const showHarmBadge = unreviewedHarmCount > 0 && (user.role === 'mentor' || user.role === 'admin');
  
  const statusColors = {
    'pending-acceptance': 'bg-yellow-100 text-yellow-700',
    'changes-proposed': 'bg-orange-100 text-orange-700',
    'created': 'bg-blue-100 text-blue-700',
    'identify-define': 'bg-purple-100 text-purple-700',
    'communicate': 'bg-orange-100 text-orange-700',
    'explore-alternatives': 'bg-amber-100 text-amber-700',
    'evaluate-select': 'bg-yellow-100 text-yellow-700',
    'agree-implement': 'bg-teal-100 text-teal-700',
    'follow-up': 'bg-cyan-100 text-cyan-700',
    'resolved': 'bg-green-100 text-green-700'
  };

  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(e); } }}
      className={`bg-white rounded-xl p-4 sm:p-6 border-2 hover:shadow-lg transition-all cursor-pointer group overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 ${
        hasUserAction ? 'border-amber-400' : 'border-stone-200 hover:border-amber-300'
      }`}
    >
      <div className="flex items-start justify-between mb-4 gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg font-medium text-stone-800 group-hover:text-amber-700 transition-colors break-words">
              {conflict.title}
            </h3>
            {hasUserAction && (
              <span className="relative flex h-3 w-3 flex-shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-500 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500"></span>
              </span>
            )}
          </div>
          <p className="text-stone-600 text-sm line-clamp-2 break-words">{conflict.problemStatement?.what || 'No description'}</p>
        </div>
        <ChevronRight className="w-5 h-5 text-stone-500 group-hover:text-amber-700 group-hover:translate-x-1 transition-all flex-shrink-0" />
      </div>

      {hasUserAction && (
        <div className="mb-3 px-3 py-2 bg-amber-50 rounded-lg border border-amber-200 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-amber-700 flex-shrink-0" />
          <span className="text-sm font-medium text-amber-700">{actionNeeded}</span>
        </div>
      )}
      {isWaitingState && (
        <div className="mb-3 px-3 py-2 bg-stone-50 rounded-lg border border-stone-200 flex items-center gap-2">
          <Clock className="w-4 h-4 text-stone-500 flex-shrink-0" />
          <span className="text-sm text-stone-500">{actionNeeded}</span>
        </div>
      )}
      {showHarmBadge && (
        <div className="mb-3 px-3 py-2 bg-red-50 rounded-lg border border-red-200 flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-red-700 flex-shrink-0" />
          <span className="text-sm font-medium text-red-700">{unreviewedHarmCount} unreviewed harm event{unreviewedHarmCount !== 1 ? 's' : ''}</span>
        </div>
      )}

      <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[conflict.status] || 'bg-stone-100 text-stone-700'}`}>
          {(conflict.status || 'pending').replace('-', ' ').replace(/\b\w/g, c => c.toUpperCase())}
        </span>
        {conflict.urgency && (
          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
            conflict.urgency >= 4 ? 'bg-red-100 text-red-700' :
            conflict.urgency === 3 ? 'bg-amber-100 text-amber-700' :
            'bg-blue-100 text-blue-700'
          }`}>
            {conflict.urgency >= 4 ? '🔴' : conflict.urgency === 3 ? '🟡' : '🔵'} {['', 'Low', 'Minor', 'Moderate', 'High', 'Critical'][conflict.urgency]}
          </span>
        )}
        <div className="flex items-center gap-1 text-stone-500 text-sm">
          <Users className="w-4 h-4 flex-shrink-0" />
          <span>{conflict.mentees?.length || 0} participants</span>
        </div>
        <div className="flex items-center gap-1 text-stone-500 text-sm">
          <Clock className="w-4 h-4 flex-shrink-0" />
          <span>{new Date(conflict.updatedAt).toLocaleDateString()}</span>
        </div>
        {conflict.needByDate && (
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            new Date(conflict.needByDate) < new Date() ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
          }`}>
            Due {new Date(conflict.needByDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
          </span>
        )}
      </div>
    </div>
  );
}

// Create Conflict View Component

export { getConflictAction, ConflictCard };
